import { useState } from 'react';
import {
  dashboardMetricCards,
  dashboardQuickActions,
  dashboardRecentActivity,
  dashboardTasks,
  dashboardRules,
  dashboardStats,
  demoRadio,
} from '@/mocks/rfwDashboardSimple';

export default function DashboardSimpleSection() {
  const [activeTab, setActiveTab] = useState<'vista-general' | 'acciones' | 'actividad' | 'tareas' | 'reglas'>('vista-general');

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-100 via-teal-100 to-amber-100 border border-emerald-200 mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-emerald-700 tracking-wide">DASHBOARD SIMPLE</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Todo lo importante en <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 bg-clip-text text-transparent">una sola pantalla</span>
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Sin menús. Sin subpáginas. Sin perder tiempo. Oyentes, ingresos, ranking, acciones y tareas — visible al instante.
          </p>
        </div>

        {/* Stats Mini */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-8">
          {[
            { value: dashboardStats.totalRadiosUsing.toLocaleString(), label: 'Radios lo usan', icon: 'ri-radio-line' },
            { value: dashboardStats.avgActionsPerDay.toString(), label: 'Acciones/día', icon: 'ri-flashlight-line' },
            { value: dashboardStats.avgTimeOnDashboard, label: 'Tiempo promedio', icon: 'ri-timer-line' },
            { value: `${dashboardStats.tasksCompletionRate}%`, label: 'Tareas hechas', icon: 'ri-check-double-line' },
            { value: dashboardStats.avgCardsViewed.toString(), label: 'Cards vistas', icon: 'ri-eye-line' },
            { value: dashboardStats.mostClickedAction, label: 'Más clickeado', icon: 'ri-cursor-line' },
          ].map((stat, i) => (
            <div key={i} className="bg-background-100 rounded-lg p-3 text-center border border-background-200/70">
              <div className="text-xs text-foreground-400 mb-0.5 flex items-center justify-center gap-1">
                <i className={`${stat.icon} text-[10px]`}></i>
                {stat.label}
              </div>
              <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-7">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70 flex-wrap justify-center gap-1">
            {(['vista-general', 'acciones', 'actividad', 'tareas', 'reglas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab
                    ? 'bg-emerald-500 text-white'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab === 'vista-general' && 'Vista General'}
                {tab === 'acciones' && 'Acciones'}
                {tab === 'actividad' && 'Actividad'}
                {tab === 'tareas' && 'Tareas'}
                {tab === 'reglas' && 'Reglas'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Vista General */}
        {activeTab === 'vista-general' && (
          <div>
            {/* Demo radio header */}
            <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-amber-50 rounded-xl p-4 mb-5 border border-emerald-200/50 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
                  {demoRadio.frequency}
                </div>
                <div>
                  <div className="font-bold text-foreground-950">{demoRadio.name}</div>
                  <div className="text-xs text-foreground-500">{demoRadio.slogan} · {demoRadio.plan} · {demoRadio.daysActive} días activa</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  En vivo · {demoRadio.streamListeners.toLocaleString()} oyentes
                </span>
              </div>
            </div>

            {/* Metric cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {dashboardMetricCards.map((card) => (
                <a
                  key={card.id}
                  href={card.link}
                  className={`${card.bgColor} rounded-xl p-4 border border-background-200/70 hover:border-emerald-200 transition-all cursor-pointer group`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-9 h-9 rounded-lg ${card.iconBg} flex items-center justify-center`}>
                      <i className={`${card.icon} ${card.textColor} text-base`}></i>
                    </div>
                    {card.trend !== 'stable' && (
                      <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        card.trend === 'up' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
                      }`}>
                        {card.trendValue}
                      </span>
                    )}
                  </div>
                  <div className={`text-lg md:text-xl font-bold ${card.textColor}`}>{card.value}</div>
                  <div className="text-[10px] text-foreground-400 mt-0.5">{card.label}</div>
                  <div className="text-[10px] text-foreground-500 mt-1">{card.subtitle}</div>
                </a>
              ))}
            </div>

            {/* Quick actions mini */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {dashboardQuickActions.slice(0, 6).map((action) => (
                <a
                  key={action.id}
                  href={action.link}
                  className={`bg-gradient-to-r ${action.gradient} rounded-lg p-3 text-white group hover:scale-[1.02] transition-transform cursor-pointer`}
                >
                  <div className="flex items-center gap-2">
                    <i className={`${action.icon} text-sm`}></i>
                    <span className="text-xs font-semibold whitespace-nowrap">{action.label}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Acciones */}
        {activeTab === 'acciones' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dashboardQuickActions.map((action) => (
                <a
                  key={action.id}
                  href={action.link}
                  className="bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-emerald-200 transition-all cursor-pointer group"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.iconGradient} flex items-center justify-center mb-4`}>
                    <i className={`${action.icon} text-white text-xl`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-1.5">{action.label}</div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{action.description}</p>
                  {action.primary && (
                    <span className="inline-flex items-center gap-1 mt-3 text-[10px] text-emerald-600 font-medium">
                      <i className="ri-star-fill text-[10px]"></i>
                      Acción principal
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Actividad */}
        {activeTab === 'actividad' && (
          <div>
            <div className="bg-background-100 rounded-xl border border-background-200/70 divide-y divide-background-200/70">
              {dashboardRecentActivity.map((act) => (
                <a
                  key={act.id}
                  href={act.entityLink}
                  className="flex items-start gap-3 p-4 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className={`w-9 h-9 rounded-lg ${act.bgColor} flex items-center justify-center shrink-0`}>
                    <i className={`${act.icon} ${act.color} text-sm`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground-950">
                      <span className="font-semibold">{act.entity}</span>
                      <span className="text-foreground-500"> {act.message}</span>
                    </div>
                    <div className="text-[10px] text-foreground-400 mt-0.5">{act.timeAgo}</div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-300 text-sm shrink-0 mt-1"></i>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Tareas */}
        {activeTab === 'tareas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {dashboardTasks.slice(0, 4).map((task) => (
                <div
                  key={task.id}
                  className="bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-emerald-200 transition-colors"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg ${
                      task.priority === 'alta' ? 'bg-rose-100' :
                      task.priority === 'media' ? 'bg-amber-100' :
                      'bg-teal-100'
                    } flex items-center justify-center shrink-0`}>
                      <i className={`${task.icon} ${
                        task.priority === 'alta' ? 'text-rose-600' :
                        task.priority === 'media' ? 'text-amber-600' :
                        'text-teal-600'
                      } text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-sm text-foreground-950">{task.title}</span>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                          task.priority === 'alta' ? 'bg-rose-100 text-rose-600' :
                          task.priority === 'media' ? 'bg-amber-100 text-amber-600' :
                          'bg-teal-100 text-teal-600'
                        }`}>
                          {task.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500 leading-relaxed mb-3">{task.description}</p>
                      <a
                        href={task.actionLink}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 cursor-pointer"
                      >
                        {task.actionLabel}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dashboardRules.map((rule) => (
                <div key={rule.id} className="bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-emerald-200 transition-colors text-center">
                  <div className={`w-12 h-12 rounded-xl bg-${rule.color}-50 flex items-center justify-center mx-auto mb-4`}>
                    <i className={`${rule.icon} text-xl text-${rule.color}-500`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{rule.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <a
            href="/dashboard-simple"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 text-white rounded-full text-sm font-bold hover:from-emerald-600 hover:via-teal-600 hover:to-amber-600 transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-emerald-500/20"
          >
            <i className="ri-dashboard-line"></i>
            Ver dashboard completo
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Final Rule */}
        <div className="mt-5 text-center">
          <p className="text-xs text-foreground-400 italic">Todo lo importante visible sin navegar.</p>
        </div>
      </div>
    </section>
  );
}